import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { ContentEditable, forwardRef, Cursor, Popover } from 'mw-style-react';
import { useIntl, useSmartChip, useQuoting } from 'hooks';
import AppUtils from '@control-front-end/utils/utils';
import getUrlAttachments from '@control-front-end/utils/getUrlAttachments';
import {
  SmartChipPopup,
  FilesDragZone,
  ContextualFormattingMenuPopup,
} from 'components';
import mes from './intl';
import './DescriptionEditor.scss';

const ForwardDescr = forwardRef(ContentEditable);

function DescriptionEditor(props) {
  const { accId, refEdit, description, access, onChange, onUpload } = props;
  const t = useIntl();

  const _focus = () => {
    if (refEdit.current) {
      refEdit.current.focus();
      Cursor.moveCursorToEnd(refEdit.current, { scroll: { block: 'end' } });
      refEdit.current.scrollTop = 99999;
    }
  };

  const {
    popup,
    popupStyle,
    popupRef,
    selectedText,
    handleMouseUp,
    handleMouseDown,
    clearSelection,
    storedRange,
  } = useQuoting({
    containerRef: refEdit,
    onQuote: () => {}, // Empty since we don't need quoting in edit mode
  });

  useEffect(() => {
    _focus();
    const selectClickedImg = (e) => {
      if (e.target.tagName !== 'IMG') return;
      const range = document.createRange();
      range.setStartBefore(e.target);
      range.setEndAfter(e.target);

      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    };

    const handleDirectMouseUp = (e) => {
      handleMouseUp(e);
    };

    const handleDirectMouseDown = (e) => {
      handleMouseDown(e);
    };

    refEdit.current?.addEventListener('click', selectClickedImg);
    refEdit.current?.addEventListener('mouseup', handleDirectMouseUp);
    refEdit.current?.addEventListener('mousedown', handleDirectMouseDown);

    return () => {
      refEdit.current?.removeEventListener('click', selectClickedImg);
      refEdit.current?.removeEventListener('mouseup', handleDirectMouseUp);
      refEdit.current?.removeEventListener('mousedown', handleDirectMouseDown);
    };
  }, [handleMouseUp, handleMouseDown]);

  const handleUpdateDescription = (value) => {
    onChange(AppUtils.htmlToBbCode(value, true));
  };

  const handleFormat = (formattedText) => {
    AppUtils.applyFormatting(
      formattedText,
      refEdit,
      storedRange,
      accId,
      onChange
    );
  };

  const {
    smartChipPopup,
    query,
    handleRemoveChipField,
    updateQuery,
    handleAddSmartChip,
    handleKeyEvent,
  } = useSmartChip({
    editorRef: refEdit,
    onRemoveSearchField: handleUpdateDescription,
    onAddSmartChip: handleUpdateDescription,
    onAddSearchField: handleUpdateDescription,
    useElementId: true,
  });

  const handleOnChange = ({ value }) => {
    updateQuery();
    const newDescription = AppUtils.htmlToBbCode(value, true);
    onChange(newDescription);
  };

  const descriptionHTML = AppUtils.bbCodeToHtml(description || '', accId);

  return (
    <>
      <FilesDragZone
        containerRef={refEdit}
        onUpload={(data) => onUpload({ value: data }, false)}
      />
      <ForwardDescr
        id="editor"
        ref={refEdit}
        dataTestId="event-description-editor"
        styleName="editor__field"
        className="editor__field"
        type="html"
        lineNumbers={false}
        value={descriptionHTML}
        placeholder={t(mes.startToWriteHere)}
        onChange={handleOnChange}
        onKeyDown={handleKeyEvent}
        onUpload={onUpload}
        onMouseUp={handleMouseUp}
        onMouseDown={handleMouseDown}
      />
      {getUrlAttachments(descriptionHTML, {
        youtube: {
          opts: {
            width: 300,
            height: 150,
          },
        },
      })}
      {smartChipPopup ? (
        <Popover
          opened
          topLevel={!window.frameElement}
          anchors={{
            binding: Popover.ANCHOR.left_top,
            content: Popover.ANCHOR.left_top,
          }}
          bindingElement={document.querySelector('div.searchChip')}
          content={
            <SmartChipPopup
              query={query}
              users={access}
              onSelect={handleAddSmartChip}
              onClose={handleRemoveChipField}
            />
          }
        >
          <div />
        </Popover>
      ) : null}
      <ContextualFormattingMenuPopup
        containerRef={refEdit}
        popupRef={popupRef}
        visible={popup}
        popupStyle={popupStyle}
        isEditMode={true}
        onQuote={() => {}}
        onFormat={handleFormat}
        selectedText={selectedText}
        clearSelection={clearSelection}
        onCreateActor={() => {}}
        storedRange={storedRange}
      />
    </>
  );
}

DescriptionEditor.propTypes = {
  refEdit: PropTypes.object,
  accId: PropTypes.string,
  description: PropTypes.string,
  access: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  onUpload: PropTypes.func,
};

export default DescriptionEditor;
